import { useEffect, useState } from 'react';
import { KeyRound } from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { CampoValidado } from '../ui/Formulario';
import CampoContrasena from '../ui/CampoContrasena';
import Ayuda from '../ui/Ayuda';

const MIN = 12;

const ROL_CORTO = { DUENO: 'dueño', ADMIN: 'administrador', ENCARGADO: 'encargado', OPERADOR: 'operador', CONSULTA: 'consulta', SOPORTE: 'soporte de TEGO' };
const etiquetaRolCorta = (rol) => ROL_CORTO[rol] || rol;
function fechaLarga(iso) {
  try {
    return new Date(iso).toLocaleString('es-MX', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}
// «07:52 p.m.» ya termina en punto: al cerrar la frase con otro salía
// «p.m..» (visto en producción el 19 de septiembre de 2026). Cierra la
// frase sin repetirlo.
function cierra(texto) {
  return /[.]$/.test(texto) ? texto : `${texto}.`;
}

export default function CambioPassword({ forzado }) {
  const { marcarPasswordCambiada, cerrarSesion, usuario } = useAuth();
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [confirma, setConfirma] = useState('');
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);
  const [cargando, setCargando] = useState(false);
  // CREDENCIALES A5 · quién emitió la temporal y hasta cuándo sirve. Si la
  // emitió otra persona, se dice (I-8): una contraseña restablecida sin
  // que uno lo pidiera es la primera señal de que algo anda mal.
  const [entrega, setEntrega] = useState(null);
  useEffect(() => {
    if (!forzado) return undefined;
    let vivo = true;
    api.get('/auth/credencial-en-curso')
      .then((d) => { if (vivo) setEntrega(d.entrega || null); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [forzado]);

  // Las mismas reglas del servidor, dichas al salir del campo. La política
  // no es solo la longitud: `validar_politica_password` rechaza también una
  // contraseña de puras letras o puros números, y eso no se decía en
  // ninguna parte — se descubría al pulsar Guardar.
  const reglas = {
    nueva: (v) => {
      if (v.length < MIN) return `Al menos ${MIN} caracteres.`;
      if (/^\d+$/.test(v) || /^[a-zA-Z]+$/.test(v)) {
        return 'Combina letras con números o símbolos.';
      }
      return null;
    },
    confirma: (v) => (v && v !== nueva ? 'No coincide con la nueva contraseña.' : null),
  };
  const listo = !reglas.nueva(nueva) && !reglas.confirma(confirma) && confirma && actual;

  async function enviar(e) {
    e.preventDefault();
    setError(null);
    setCargando(true);
    try {
      await api.post('/auth/cambiar-password', {
        password_actual: actual,
        password_nueva: nueva,
      });
      setOk(true);
      marcarPasswordCambiada();
    } catch (err) {
      setError(err.message || 'No se pudo cambiar la contraseña.');
    } finally {
      setCargando(false);
    }
  }

  return (
    <div className="consola min-h-dvh bg-[var(--color-lienzo)] flex flex-col justify-center items-center p-4 font-sans text-gray-800">
      <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-8 space-y-6 border border-gray-200">
        <div className="text-center">
          <div className="bg-[var(--color-marca)] w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-sm">
            <KeyRound className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 tracking-tight">
            {forzado ? 'Fija tu contraseña' : 'Cambiar contraseña'}
          </h2>
          {forzado && (
            <p className="text-gray-500 mt-1 text-sm font-medium">
              Entraste con una contraseña temporal. Elige la tuya y ya puedes trabajar; hasta entonces no se carga nada más.
            </p>
          )}
        </div>

        {forzado && entrega && (
          <div className={`text-sm rounded-lg border p-3 ${entrega.propia ? 'bg-gray-50 border-gray-200 text-gray-700' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
            {entrega.propia
              ? <>Esta contraseña temporal la pediste tú el {cierra(fechaLarga(entrega.emitida_en))}</>
              : <>Esta contraseña temporal la emitió <b>{entrega.emitida_por_nombre || 'alguien de tu empresa'}</b>{entrega.emitida_por_rol ? ` (${etiquetaRolCorta(entrega.emitida_por_rol)})` : ''} el {cierra(fechaLarga(entrega.emitida_en))} <b>Si no lo pediste, avisa a tu encargado hoy mismo.</b></>}
            {entrega.expira_en && <span className="block text-xs mt-1">Sirve hasta el {fechaLarga(entrega.expira_en)}; después habrá que pedir otra.</span>}
          </div>
        )}

        {ok && !forzado ? (
          <div className="space-y-3">
            <div role="status" className="text-sm text-green-800 bg-green-50 border border-green-200 rounded p-3 text-center">
              Contraseña actualizada correctamente.
            </div>
            <OfrecerCerrarLasDemas />
          </div>
        ) : (
          <form onSubmit={enviar} className="space-y-4">
            {/* Los tres campos tenían un `<label>` suelto, sin `htmlFor` y sin
                envolver al control: al pulsarlo no pasaba nada, y un lector de
                pantalla anunciaba tres «contraseña» idénticos sin decir cuál
                era cuál. `CampoValidado` envuelve el control en su etiqueta. */}
            <CampoValidado etiqueta={forzado ? 'La contraseña temporal que te dieron' : 'Contraseña actual'} requerido valor={actual}>
              {(props) => <CampoContrasena {...props} value={actual}
                                           autoComplete="current-password" placeholder="••••••••"
                                           onChange={(e) => setActual(e.target.value)} />}
            </CampoValidado>

            <CampoValidado etiqueta="Nueva contraseña" requerido valor={nueva}
                           ayuda={`Mínimo ${MIN} caracteres, con números o símbolos.`}
                           validar={reglas.nueva}>
              {(props) => <CampoContrasena {...props} value={nueva}
                                           autoComplete="new-password" placeholder="••••••••••••"
                                           onChange={(e) => setNueva(e.target.value)} />}
            </CampoValidado>

            <CampoValidado etiqueta="Confirmar nueva contraseña" requerido valor={confirma}
                           validar={reglas.confirma}>
              {(props) => <CampoContrasena {...props} value={confirma}
                                           autoComplete="new-password" placeholder="••••••••••••"
                                           onChange={(e) => setConfirma(e.target.value)} />}
            </CampoValidado>

            {error && (
              <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2.5">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={cargando || !listo}
              className="w-full bg-[var(--color-marca)] hover:bg-[var(--color-marca-fuerte)] disabled:opacity-60 text-white font-semibold py-3 px-4 rounded text-sm transition-colors"
            >
              {cargando ? 'Guardando…' : (forzado ? 'Fijar mi contraseña y entrar' : 'Actualizar contraseña')}
            </button>
          </form>
        )}

        <button
          type="button"
          onClick={cerrarSesion}
          className="w-full text-xs font-semibold text-gray-500 hover:text-gray-800"
        >
          Cerrar sesión ({usuario?.nombre})
        </button>
      </div>
    </div>
  );
}


// =====================================================================
// AL CAMBIAR LA CONTRASEÑA, OFRECER CERRAR LAS DEMÁS · TODO-SIRVE F2-B
// =====================================================================
// Quien cambia su contraseña porque alguien más la sabía necesita, en el
// mismo momento, echar a ese alguien: una sesión ya abierta NO se cierra
// sola por cambiar la contraseña. Se cuenta cuántas hay en otros aparatos
// (`GET /auth/sesiones`, la misma lista de Mi cuenta) y se ofrece
// `POST /auth/sesiones/cerrar-las-demas`, que deja viva ésta.
export function OfrecerCerrarLasDemas() {
  const [otras, setOtras] = useState(null);      // null = contando
  const [cerrando, setCerrando] = useState(false);
  const [hecho, setHecho] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let vivo = true;
    api.get('/auth/sesiones')
      .then((d) => { if (vivo) setOtras((d?.sesiones || []).filter((x) => !x.es_esta).length); })
      // Si no se pudo contar, se ofrece igual: cerrar las demás no hace daño.
      .catch(() => { if (vivo) setOtras(-1); });
    return () => { vivo = false; };
  }, []);

  async function cerrar() {
    setError(null); setCerrando(true);
    try {
      const d = await api.post('/auth/sesiones/cerrar-las-demas', {});
      setHecho(d.mensaje || 'Listo: se cerraron las demás sesiones. Ésta sigue abierta.');
      setOtras(0);
    } catch (e) {
      setError(`${e.message || 'No se cerraron las demás sesiones.'} Revisa la conexión y vuelve a intentarlo.`);
    } finally {
      setCerrando(false);
    }
  }

  if (hecho) {
    return <div role="status" className="text-sm text-green-800 bg-green-50 border border-green-200 rounded p-3">{hecho}</div>;
  }
  if (otras === null) {
    return <p role="status" className="text-sm text-gray-500 text-center">Revisando tus otras sesiones…</p>;
  }
  if (otras === 0) {
    return <p className="text-sm text-gray-600 text-center">Ésta es tu única sesión abierta: no hay otros aparatos que cerrar.</p>;
  }
  return (
    <div className="border border-amber-200 bg-amber-50 rounded p-3 space-y-2">
      <p className="text-sm text-amber-900">
        {otras > 0
          ? `Tienes ${otras === 1 ? '1 sesión abierta' : `${otras} sesiones abiertas`} en otros aparatos, y siguen abiertas con la contraseña cambiada.`
          : 'Las sesiones abiertas en otros aparatos siguen abiertas con la contraseña cambiada.'}
        {' '}Si alguien más conocía la contraseña anterior, ciérralas aquí.
      </p>
      <button type="button" onClick={cerrar} disabled={cerrando}
              className="w-full min-h-[44px] px-3 py-2 text-sm font-semibold rounded border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 disabled:opacity-60">
        {cerrando ? 'Cerrando…' : 'Cerrar las demás sesiones'}
      </button>
      <Ayuda id="password.cerrar_las_demas" />
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    </div>
  );
}
